import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  Input,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import { noWhitespaceValidator } from 'src/app/common/utils/form-validators';
import { take, finalize } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'src/app/services/user.service';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { SUCCESSES } from 'src/app/common/utils/notification-constants';
import { User, LtiSetting } from '../../../models/user';
import { SettingsNotificationService } from '../../../settings/shared/services/settings-notifications.service';
import {
  IntegrationsFormService,
  FormAction,
} from '../user-settings-integrations/services/integrations-form.service';

@UntilDestroy()
@Component({
  selector: 'app-user-settings-lti',
  templateUrl: './user-settings-lti.component.html',
  styleUrls: ['./user-settings-lti.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsLtiComponent implements OnInit {
  @Input() form!: UntypedFormGroup;

  user?: User;
  ltiSettingsArray!: UntypedFormArray;
  protected readonly IconTypes = IconTypes;

  readonly ltiConfigurationUrl = 'https://apis.pencilapp.com/lti/register';

  constructor(
    private userService: UserService,
    private settingsNotificationService: SettingsNotificationService,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private integrationsFormService: IntegrationsFormService,
    private clipboard: Clipboard,
    private notificationToasterService: NotificationToasterService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res?.user;
      this.initForm();
    });

    // Handle form actions from integrations service
    this.integrationsFormService
      .getFormAction$()
      .pipe(untilDestroyed(this))
      .subscribe((action: FormAction) => {
        if (action === FormAction.SUBMIT) {
          this.handleSubmit();
        } else if (action === FormAction.CANCEL) {
          this.handleCancel();
        }
      });
  }

  private initForm(): void {
    // Initialize FormArray reference
    this.ltiSettingsArray = this.form.get('ltiSettings') as UntypedFormArray;

    this.patchFormWithValues();

    // Register form with integrations service
    const initialValue = this.form.getRawValue();
    this.integrationsFormService.registerForm(this.form, initialValue);
  }

  private handleSubmit(): void {
    if (!this.user) {
      return;
    }

    const formValue = this.form.getRawValue();
    const ltiSettings = formValue.ltiSettings || [];

    // Filter out empty settings
    const validLtiSettings = ltiSettings.filter(
      (setting: LtiSetting) => setting.client_id?.trim() && setting.deployment_id?.trim(),
    );

    const updatedUser = {
      ...this.user,
      institution: {
        ...this.user?.institution,
        ltiSettings: validLtiSettings,
      },
    } as User;

    this.userService
      .updateInstitution(updatedUser)
      .pipe(
        take(1),
        finalize(() => this.integrationsFormService.setLoading(false)),
      )
      .subscribe({
        next: async () => {
          // Refresh user data from backend to ensure consistency
          await this.userService.refreshUser();
          // Update form to reflect the actual saved settings (without invalid entries)
          this.integrationsFormService.updateInitialValue(this.form.getRawValue());
          this.settingsNotificationService.showSuccessSettingsUpdateNotification();
          this.cdr.markForCheck();
        },
        error: () => {
          this.settingsNotificationService.showFailureSettingsUpdateNotification();
          this.cdr.markForCheck();
        },
      });
  }

  private handleCancel(): void {
    // Re-patch form array to restore removed items
    this.patchFormWithValues();
    this.cdr.markForCheck();
  }

  private patchFormWithValues(): void {
    const existingLtiSettings = this.user?.institution?.ltiSettings || [];

    // Clear existing form array
    this.ltiSettingsArray.clear();

    // Add existing settings to the form
    existingLtiSettings.forEach((setting: LtiSetting) => {
      this.addLtiSetting(setting);
    });
  }

  addLtiSetting(setting?: LtiSetting): void {
    const ltiSettingGroup = this.formBuilder.group({
      name: [setting?.name || ''],
      client_id: [setting?.client_id || '', [Validators.required, noWhitespaceValidator]],
      deployment_id: [setting?.deployment_id || '', [Validators.required, noWhitespaceValidator]],
    });

    // Insert at the beginning if no setting provided (user clicked Add button)
    // Otherwise, push to the end (loading existing settings from backend)
    if (setting) {
      this.ltiSettingsArray.push(ltiSettingGroup);
    } else {
      this.ltiSettingsArray.insert(0, ltiSettingGroup);
    }
  }

  removeLtiSetting(index: number): void {
    this.ltiSettingsArray.removeAt(index);
  }

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
    this.notificationToasterService.showDefaultSuccessNotification(
      this.translateService.instant('Copied LTI URL to clipboard'),
      SUCCESSES.API_KEY_COPIED,
    );
  }
}
